import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Container,
  Divider,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

const Settings: React.FC = () => {
  const { organization } = useAuth();
  const [fiscalYearStartMonth, setFiscalYearStartMonth] = useState<number>(4);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const monthNames = [
    '1月', '2月', '3月', '4月', '5月', '6月',
    '7月', '8月', '9月', '10月', '11月', '12月'
  ];

  // 初期値の取得
  useEffect(() => {
    const fetchSettings = async () => {
      if (!organization?.id) return;

      try {
        const { data, error } = await supabase
          .from('organizations')
          .select('fiscal_year_start_month')
          .eq('id', organization.id)
          .single();

        if (error) throw error;
        
        if (data?.fiscal_year_start_month) {
          setFiscalYearStartMonth(data.fiscal_year_start_month);
        }
      } catch (err) {
        console.error('設定の取得エラー:', err);
      }
    };

    fetchSettings();
  }, [organization?.id]);

  const handleSave = async () => {
    if (!organization?.id) return;

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const { error } = await supabase
        .from('organizations')
        .update({ fiscal_year_start_month: fiscalYearStartMonth })
        .eq('id', organization.id);

      if (error) throw error;

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.message || '設定の保存に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        組織設定
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mb: 4 }}>
        {organization?.name || 'Grand Mansion Management Association'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          設定を保存しました
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            会計年度設定
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            会計年度の開始月を設定します。この設定に基づいてダッシュボードの年別集計が行われます。
          </Typography>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="fiscal-year-start-label">会計年度開始月</InputLabel>
            <Select
              labelId="fiscal-year-start-label"
              value={fiscalYearStartMonth}
              label="会計年度開始月"
              onChange={(e) => setFiscalYearStartMonth(Number(e.target.value))}
            >
              {monthNames.map((month, index) => (
                <MenuItem key={index + 1} value={index + 1}>
                  {month}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            例: 4月を選択した場合、当年度は当年4月〜翌年3月となります
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? '保存中...' : '設定を保存'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Settings;